/**
 * @file Vue du widget embarquable de bons cadeaux (catalogue, filtres, tri, pagination).
 * @module widgets.GiftVoucher.view.GiftVoucherView
 *
 * Affiche un catalogue de bons cadeaux avec grille responsive, filtres par
 * catégorie et par prix, tri (popularité, prix, nom), pagination et insertion
 * du bon cadeau personnalisé à une position configurable.
 */

import React, {useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSearchParams} from 'react-router-dom';

// MUI Components
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';
import {useTheme, useMediaQuery} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// API Hooks
import {
  useExternalGiftVoucherTypes,
  useExternalCatalogCategories,
  useExternalCustomVoucherConfig,
} from 'packages/common/hooks/api/widgets/giftVouchers/GiftVouchers';
import {getCurrencySymbol} from 'packages/common/config/currencies';

// Common hooks and components
import {useWidgetBase} from '../../common/hooks/useWidgetBase';
import {WidgetStateHandler} from '../../common/components/WidgetStateHandler';
import WidgetErrorBoundary from '../../common/components/WidgetErrorBoundary';
import {IframeResizeProvider} from '../../common/contexts/IframeResizeContext';
import {BrandingFooter} from 'packages/common/components/branding-footer';
import {createStripeNotConfiguredError, createGiftVouchersDisabledError} from '../../common/hooks/useWidgetError';
import {getLocalizedText} from 'packages/common/utils/multiLanguageHelper';
import {isGiftVoucherOnlineSaleAvailable} from 'packages/common/utils/giftVoucherActivationHelper';
import {FlowLanguageSelector} from 'packages/common/components/flow-language-selector';
import {useWidgetLanguageSetup} from '../../common/hooks/useWidgetLanguageSetup';

// Components
import GiftVoucherGrid from '../components/GiftVoucherGrid';
import NoVouchersDisplay from '../components/NoVoucherDisplay';
import GiftVoucherFilters from '../components/GiftVoucherFilters';
import GiftVoucherPagination from '../components/GiftVoucherPagination';

// Utils
import {isInIframe} from '../../../utils/iframeUtils';
import {sendScrollToTopMessage} from '../../common/utils/iframeMessaging';
import {sortVouchers, filterVouchersByPrice, calculatePriceRange} from '../utils/voucherSorting';
import {TIMING} from '../../../constants/timing';

// Config
import {getWidgetDimensions, WIDGET_TYPES} from '../../config';

// Styled components
import {
  WidgetContainer,
  WidgetHeader,
  FixedHeaderContainer,
  HeaderSpacerBox,
  ScrollableContentBox,
} from '../../common/components/styled/WidgetViewStyles';

/**
 * Composant principal du widget de bons cadeaux
 *
 * @param {Object} props - Props du composant
 * @param {string} [props.mode='fixed'] - Mode d'affichage ('fixed' ou 'floating')
 * @param {string} [props.companyId] - ID de l'entreprise (priorité sur URL)
 * @param {string} [props.apiKey] - Clé API (priorité sur URL)
 * @param {string} [props.instanceId] - ID d'instance pour multi-widgets
 * @return {JSX.Element} Widget de bons cadeaux
 */
export default function GiftVoucherView({
  mode = 'fixed',
  companyId: propCompanyId = null,
  apiKey: propApiKey = null,
  instanceId = 'default-instance',
}) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');

  // Extraction des paramètres depuis URL ou props
  const embedded = isInIframe();
  const displayMode = (urlMode || mode).toLowerCase().trim();
  const companyId = propCompanyId || searchParams.get('companyId') || null;
  const apiKey = propApiKey || searchParams.get('apiKey') || null;
  const urlInstanceId = searchParams.get('instanceId');
  const effectiveInstanceId = urlInstanceId || instanceId;
  const customColor = searchParams.get('customColor');

  // Configuration des dimensions de l'iframe selon le mode
  const dimensions = getWidgetDimensions(WIDGET_TYPES.GIFTVOUCHERS, displayMode);

  return (
    <IframeResizeProvider
      mode={displayMode}
      instanceId={effectiveInstanceId}
      minHeight={dimensions.minHeight}
      maxHeight={dimensions.maxHeight}
      debounceDelay={dimensions.debounceDelay}
    >
      <WidgetErrorBoundary widgetType={WIDGET_TYPES.GIFTVOUCHERS}>
        <GiftVoucherContent
          displayMode={displayMode}
          embedded={embedded}
          companyId={companyId}
          apiKey={apiKey}
          instanceId={effectiveInstanceId}
          customColor={customColor}
        />
      </WidgetErrorBoundary>
    </IframeResizeProvider>
  );
}

/**
 * Contenu interne du widget de bons cadeaux
 * Séparé du wrapper pour permettre l'accès au contexte IframeResize
 *
 * @return {JSX.Element} Contenu du widget
 */
function GiftVoucherContent({
  displayMode,
  embedded,
  companyId: propCompanyId,
  apiKey,
  instanceId,
  customColor,
}) {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || propCompanyId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Hook de base : company, primaryColor, resize, close handler
  // Note: skipHeightUpdate=true car ce widget a une gestion plus complexe du resize
  const {
    containerRef,
    forceResize,
    updateHeight,
    companyDetails,
    isCompanyLoading,
    companyError,
    handleCloseWidget,
    primaryColor,
  } = useWidgetBase({
    companyId,
    apiKey,
    embedded,
    displayMode,
    instanceId,
    customColor,
    skipHeightUpdate: true, // Gestion manuelle car dépendances complexes
  });

  // Hook de traduction et gestion de la langue
  const {
    getGenericText,
    currentLanguage,
    isMultiLanguageEnabled,
    enabledLanguages,
    isLoading: isTranslationsLoading,
    handleLanguageChange,
  } = useWidgetLanguageSetup({
    apiKey,
    widgetType: 'giftVoucher',
    companyDetails,
    embedded,
    instanceId,
    searchParams,
  });

  // Textes traduits (tous hardcodés sauf le bouton flottant)
  const widgetTitle = getGenericText('widgets.giftVoucher.title');
  const loadingMessage = getGenericText('common.loading');
  const closeAriaLabel = getGenericText('common.buttons.close');

  // API : Types de bons cadeaux (avec langue courante pour traductions)
  const {
    giftVoucherTypes,
    isLoading: isVoucherTypesLoading,
    isError: isVoucherTypesError,
    error: voucherTypesError,
  } = useExternalGiftVoucherTypes(apiKey, currentLanguage);

  // API : Catégories du catalogue
  const {
    categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useExternalCatalogCategories(apiKey);

  // API : Configuration du bon personnalisé
  const {customVoucherConfig} = useExternalCustomVoucherConfig(apiKey);

  // Détermination de l'erreur prioritaire (company > module désactivé > vouchers > categories > Stripe)
  // Note: Pour les bons cadeaux, Stripe EST obligatoire
  const currentError = useMemo(() => {
    if (companyError) return companyError;
    // Module bons cadeaux désactivé ou vente en ligne coupée : priorité juste
    // après l'erreur company, avant tout chargement de catalogue.
    if (companyDetails && !isGiftVoucherOnlineSaleAvailable(companyDetails)) {
      // Message personnalisé par l'établissement (multilingue), localisé sur la langue courante
      const customMessage = getLocalizedText(
          companyDetails?.modulesActivation?.giftVouchers?.onlineSaleDisabledMessage,
          {lang: currentLanguage},
      );
      // Même source que la page de vente publique : clés i18n giftVoucher.unavailable.*,
      // avec le message personnalisé de l'établissement en surcharge du défaut.
      return createGiftVouchersDisabledError(WIDGET_TYPES.GIFTVOUCHERS, {
        title: getGenericText('giftVoucher.unavailable.title'),
        message: customMessage || getGenericText('giftVoucher.unavailable.message'),
      });
    }
    if (isVoucherTypesError) return voucherTypesError;
    if (isCategoriesError) return categoriesError;
    // Vérification Stripe (obligatoire pour les bons cadeaux)
    if (companyDetails && !companyDetails?.stripeAccountId) {
      return createStripeNotConfiguredError(WIDGET_TYPES.GIFTVOUCHERS);
    }
    return null;
  }, [companyError, isVoucherTypesError, voucherTypesError, isCategoriesError, categoriesError, companyDetails, currentLanguage, getGenericText]);

  // État des filtres
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [sortBy, setSortBy] = React.useState('default');

  // Calcul de la plage de prix disponible
  const priceRangeData = useMemo(() => {
    return calculatePriceRange(giftVoucherTypes);
  }, [giftVoucherTypes]);

  const [priceRange, setPriceRange] = React.useState([priceRangeData.min, priceRangeData.max]);

  // Mise à jour de la plage de prix quand les bons changent
  useEffect(() => {
    setPriceRange([priceRangeData.min, priceRangeData.max]);
  }, [priceRangeData.min, priceRangeData.max]);

  // Filtrage et tri des bons cadeaux
  const filteredVouchers = useMemo(() => {
    if (!giftVoucherTypes) return [];

    // 1. Filtrer par disponibilité et exclure le type custom_voucher
    let filtered = giftVoucherTypes.filter((voucher) => {
      if (voucher.type === 'custom_voucher') return false;
      // Support nouveau et ancien format
      return voucher.isAvailable !== undefined ? voucher.isAvailable : voucher.typeStatus;
    });

    // 2. Filtrer par catégories sélectionnées
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((voucher) =>
        selectedCategories.some((categoryId) => voucher.categories?.includes(categoryId)),
      );
    }

    // 3. Filtrer par prix
    filtered = filterVouchersByPrice(filtered, priceRange);

    // 4. Trier (featured en priorité en mode fixed uniquement)
    filtered = sortVouchers(filtered, sortBy, displayMode);

    return filtered;
  }, [giftVoucherTypes, selectedCategories, priceRange, sortBy, displayMode]);

  // Réinitialisation des filtres
  const handleFiltersReset = useCallback(() => {
    setSelectedCategories([]);
    setPriceRange([priceRangeData.min, priceRangeData.max]);
    setSortBy('default');
  }, [priceRangeData.min, priceRangeData.max]);

  // Création de la liste virtuelle avec le bon personnalisé à la bonne position
  const virtualVoucherList = useMemo(() => {
    // Si le custom voucher n'est pas activé, retourner juste la liste filtrée
    const hasCustomVoucherEnabled = customVoucherConfig?.enabled === true;
    if (!hasCustomVoucherEnabled) {
      return filteredVouchers.map((voucher) => ({
        type: 'voucher',
        data: voucher,
      }));
    }

    // Extraire la configuration de positionnement
    const displayPosition = customVoucherConfig?.displayPosition || 'first';
    const displayAfterTypeId = customVoucherConfig?.displayAfterTypeId || null;

    // Calculer l'index d'insertion du bon personnalisé
    let insertIndex = 0;
    if (displayPosition === 'first') {
      insertIndex = 0;
    } else if (displayPosition === 'last') {
      insertIndex = filteredVouchers.length;
    } else if (displayPosition === 'after' && displayAfterTypeId) {
      const afterIndex = filteredVouchers.findIndex(
          (v) => v._id === displayAfterTypeId || v.id === displayAfterTypeId,
      );
      insertIndex = afterIndex >= 0 ? afterIndex + 1 : 0;
    }

    // Construire la liste virtuelle en insérant le custom voucher
    const virtualList = [];
    let voucherIndex = 0;
    const totalItems = filteredVouchers.length + 1;

    for (let i = 0; i < totalItems; i++) {
      if (i === insertIndex) {
        virtualList.push({type: 'custom', data: null});
      } else {
        if (voucherIndex < filteredVouchers.length) {
          virtualList.push({type: 'voucher', data: filteredVouchers[voucherIndex]});
          voucherIndex++;
        }
      }
    }

    return virtualList;
  }, [filteredVouchers, customVoucherConfig]);

  // Pagination sur la liste virtuelle
  const [page, setPage] = React.useState(1);
  const itemsPerPage = isMobile ? 12 : 20;

  // Reset de la page quand les filtres changent
  useEffect(() => {
    setPage(1);
  }, [virtualVoucherList.length, selectedCategories, priceRange, sortBy]);

  const totalPages = Math.ceil(virtualVoucherList.length / itemsPerPage);
  const paginatedVouchers = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return virtualVoucherList.slice(start, start + itemsPerPage);
  }, [virtualVoucherList, page, itemsPerPage]);

  // Gestion du changement de page avec scroll
  const gridRef = React.useRef();
  const handlePageChange = useCallback((event, value) => {
    setPage(value);
    // Scroll vers le haut de l'iframe parent (mode embedded)
    if (embedded) {
      sendScrollToTopMessage(instanceId);
    }
    // Le scroll interne est géré par GiftVoucherPagination via scrollTo({top: 0})
  }, [embedded, instanceId]);

  // Mise à jour de la hauteur iframe (gestion complexe avec multiples dépendances)
  useEffect(() => {
    if (embedded && !isCompanyLoading && !isVoucherTypesLoading && !currentError) {
      const timer = setTimeout(() => {
        updateHeight();
      }, TIMING.CONTENT_CHANGE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [
    displayMode,
    embedded,
    updateHeight,
    isCompanyLoading,
    isVoucherTypesLoading,
    currentError,
    filteredVouchers.length,
    selectedCategories.length,
    priceRange,
    sortBy,
    page,
  ]);

  // Gestion des états loading/error via WidgetStateHandler
  return (
    <WidgetStateHandler
      isLoading={isCompanyLoading || isVoucherTypesLoading || isTranslationsLoading}
      error={currentError}
      embedded={embedded}
      displayMode={displayMode}
      containerRef={containerRef}
      onClose={handleCloseWidget}
      widgetType={WIDGET_TYPES.GIFTVOUCHERS}
      primaryColor={primaryColor}
      loadingMessage={loadingMessage}
    >
      {/* Contenu principal du catalogue */}
      <Box sx={{
        ...(!embedded && {
          paddingTop: 4,
        }),
      }}>
        <WidgetContainer
          embedded={embedded}
          displayMode={displayMode}
          ref={containerRef}
          maxWidth="100%"
          maxHeight="100%"
          widgetType={WIDGET_TYPES.GIFTVOUCHERS}
          data-widget-type={WIDGET_TYPES.GIFTVOUCHERS}
        >
          {/* Header pour le mode embedded non-fixed */}
          {embedded && displayMode !== 'fixed' && (
            <>
              <FixedHeaderContainer embedded={embedded} displayMode={displayMode}>
                <WidgetHeader primaryColor={primaryColor} variant="default">
                  <Typography variant="subtitle1" sx={{fontWeight: 'bold', flexGrow: 1}}>
                    {widgetTitle}
                  </Typography>

                  {/* Sélecteur de langue */}
                  {isMultiLanguageEnabled && enabledLanguages && (
                    <FlowLanguageSelector
                      currentLanguage={currentLanguage}
                      availableLanguages={enabledLanguages}
                      onLanguageChange={handleLanguageChange}
                      primaryColor={primaryColor}
                      ariaLabel={getGenericText('widgets.languageSelector.ariaLabel')}
                    />
                  )}

                  {displayMode === 'floating' && (
                    <IconButton
                      size="small"
                      onClick={handleCloseWidget}
                      sx={{color: 'inherit'}}
                      aria-label={closeAriaLabel}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </WidgetHeader>
              </FixedHeaderContainer>
              <HeaderSpacerBox hasSteps={false} embedded={embedded} />
            </>
          )}

          {/* Contenu scrollable */}
          <ScrollableContentBox hasFooter={false} embedded={embedded} displayMode={displayMode}>
            <Fade in timeout={300} onEntered={forceResize}>
              <Box ref={gridRef}>
                {/* Filtres (masqués en mode floating) */}
                {displayMode !== 'floating' && (
                  <GiftVoucherFilters
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                    maxPrice={priceRangeData.max}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    categories={categories}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    isCategoriesLoading={isCategoriesLoading}
                    primaryColor={primaryColor}
                    embedded={embedded}
                    displayMode={displayMode}
                    onReset={handleFiltersReset}
                    t={getGenericText}
                    currencySymbol={getCurrencySymbol(companyDetails?.currency)}
                    isMultiLanguageEnabled={isMultiLanguageEnabled}
                    enabledLanguages={enabledLanguages}
                    currentLanguage={currentLanguage}
                    onLanguageChange={handleLanguageChange}
                  />
                )}

                {/* Affichage des bons cadeaux */}
                {paginatedVouchers.length === 0 ? (
                  <NoVouchersDisplay t={getGenericText} />
                ) : (
                  <>
                    <GiftVoucherGrid
                      primaryColor={primaryColor}
                      voucherTypes={paginatedVouchers}
                      companyDetails={companyDetails}
                      categories={categories}
                      customVoucherConfig={customVoucherConfig}
                      displayMode={displayMode}
                      currentLanguage={currentLanguage}
                    />
                    <GiftVoucherPagination
                      page={page}
                      totalPages={totalPages}
                      onChange={handlePageChange}
                      primaryColor={primaryColor}
                      t={getGenericText}
                    />
                  </>
                )}

                {/* Footer branding */}
                <Box sx={{mb: 1}}><BrandingFooter widgetType={WIDGET_TYPES.GIFTVOUCHERS} t={getGenericText} /></Box>
              </Box>
            </Fade>
          </ScrollableContentBox>
        </WidgetContainer>
      </Box>
    </WidgetStateHandler>
  );
}

GiftVoucherView.propTypes = {
  mode: PropTypes.oneOf(['fixed', 'floating']),
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string,
};

GiftVoucherContent.propTypes = {
  displayMode: PropTypes.oneOf(['fixed', 'floating']).isRequired,
  embedded: PropTypes.bool.isRequired,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  customColor: PropTypes.string,
};
